import type { AdminMeResponse } from '@diana/contracts';
import {
  Aperture,
  LayoutDashboard,
  BookOpenText,
  Camera,
  ContactRound,
  FileText,
  Globe,
  Images,
  LoaderCircle,
  LogOut,
  Settings,
  Tag,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';

import { PublicationEntry } from './PublicationEntry.js';
import './StudioShell.css';

const crmSections = [
  ['Dashboard', '/crm/dashboard', LayoutDashboard],
  ['Shoots', '/crm/shoots', Aperture],
  ['Clients', '/crm/clients', ContactRound],
] as const;
const contentSections = [
  ['Pages', '/pages', FileText],
  ['Packages', '/packages', Tag],
  ['Galleries', '/shoots', Camera],
  ['Journal', '/journal', BookOpenText],
  ['Media', '/settings/media', Images],
] as const;

type ShellProps = {
  user: AdminMeResponse['user'];
  pendingCount?: number;
  newRequestCount?: number;
  pendingLoading?: boolean;
  onPendingChanges?(): void;
  publishing?: boolean;
  onLogout?(): void;
  contextRail?: ReactNode;
  publishPanel?: ReactNode;
  children?: ReactNode;
};

export function Shell({
  user,
  pendingCount = 0,
  newRequestCount = 0,
  pendingLoading = false,
  onPendingChanges,
  publishing = false,
  onLogout,
  contextRail,
  publishPanel,
  children,
}: ShellProps) {
  const { pathname } = useLocation();
  const siteCurrent = !pathname.startsWith('/crm/');
  const contentEditor =
    /^\/(pages|packages|shoots|journal)\/[^/]+\/?$/.test(pathname) || pathname === '/settings';
  return (
    <div
      className="admin-shell studio-shell"
      data-mobile-publish={contentEditor ? 'true' : undefined}
    >
      <aside className="admin-sidebar">
        <div className="admin-brand">
          <img
            className="admin-brand__monogram"
            src="/icons/admin-heart.svg"
            alt=""
            width="34"
            height="34"
          />
          <span className="admin-brand__name">
            <strong>Perova Photo</strong>
            <small>Admin</small>
          </span>
        </div>
        <nav aria-label="Admin sections">
          <div className="admin-nav__group">
            <span className="admin-nav__label">Studio</span>
            {crmSections.map(([label, href, Icon]) => (
              <NavLink key={href} to={href}>
                <Icon size={19} aria-hidden="true" />
                <span>{label}</span>
                {href === '/crm/shoots' && newRequestCount > 0 ? (
                  <small className="nav-request-count">{newRequestCount}</small>
                ) : null}
              </NavLink>
            ))}
          </div>
          <div className="admin-nav__group admin-nav__group--content">
            <span className="admin-nav__label">Site</span>
            {contentSections.map(([label, href, Icon]) => (
              <NavLink key={href} to={href}>
                <Icon size={19} aria-hidden="true" />
                <span>{label}</span>
              </NavLink>
            ))}
          </div>
        </nav>
        <div className="admin-sidebar__bottom">
          <PublicationEntry
            pendingCount={pendingCount}
            pendingLoading={pendingLoading}
            publishing={publishing}
            onOpen={onPendingChanges}
          />
          <div className="admin-account">
            <span className="admin-identity__initial">{user.email.slice(0, 1).toUpperCase()}</span>
            <Link
              to="/settings"
              aria-label="Settings"
              aria-current={
                pathname.startsWith('/settings') && !pathname.startsWith('/settings/media')
                  ? 'page'
                  : undefined
              }
            >
              <span>
                <strong>{user.email}</strong>
                <small>{user.role}</small>
              </span>
              <Settings size={17} aria-hidden="true" />
            </Link>
            <button type="button" onClick={onLogout} aria-label="Sign out" title="Sign out">
              <LogOut size={18} aria-hidden="true" />
            </button>
          </div>
        </div>
      </aside>
      <div className="admin-workspace">
        <div className="admin-stage" data-context={contextRail ? 'true' : 'false'}>
          <main className="admin-main" tabIndex={-1}>
            {children ?? <Outlet />}
          </main>
          {contextRail ? (
            <aside className="admin-context" aria-label="Context">
              {contextRail}
            </aside>
          ) : null}
        </div>
      </div>
      {contentEditor ? (
        <div className="mobile-editor-publish">
          <button
            className="button button--primary"
            type="button"
            disabled={pendingLoading}
            aria-busy={publishing || undefined}
            onClick={onPendingChanges}
          >
            {publishing ? (
              <LoaderCircle className="publish-spinner" size={17} aria-hidden="true" />
            ) : null}
            {pendingLoading
              ? 'Checking changes…'
              : publishing
                ? 'Publishing…'
                : pendingCount === 0
                  ? 'Republish website…'
                  : `Publish ${pendingCount} change${pendingCount === 1 ? '' : 's'}…`}
          </button>
        </div>
      ) : null}
      <nav className="admin-mobile-tabs" aria-label="Main">
        {crmSections.map(([label, href, Icon]) => (
          <NavLink
            key={href}
            to={href}
            aria-label={
              href === '/crm/shoots' && newRequestCount > 0 ? `Shoots ${newRequestCount}` : label
            }
          >
            <span className="admin-mobile-tabs__icon">
              <Icon size={22} aria-hidden="true" />
              {href === '/crm/shoots' && newRequestCount > 0 ? (
                <small className="nav-request-count">{newRequestCount}</small>
              ) : null}
            </span>
            <span>{label}</span>
          </NavLink>
        ))}
        <Link to="/site" aria-current={siteCurrent ? 'page' : undefined}>
          <Globe size={22} aria-hidden="true" />
          <span>Site</span>
        </Link>
      </nav>
      {publishPanel}
    </div>
  );
}
